import { getSession, signIn } from 'next-auth/react'

/**
 * An HTTP failure that still carries its status.
 *
 * Callers were reduced to regex-matching the message to tell a refusal from a
 * fault -- `/administrator|licence|403/i` against prose the backend is free to
 * reword. A copy edit would then have silently turned a lens refusal into a red
 * "something broke" toast. The status is the fact; the message is for the reader.
 */
export class ApiError extends Error {
  readonly status: number
  readonly detail: unknown

  constructor(status: number, message: string, detail?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const session = await getSession()

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    // `ApiError`, not `Error`: a caller distinguishing a refusal from a fault
    // was previously reduced to regex-matching this message, and the backend is
    // free to reword it. The status travels so that cannot rot.
    const detail = errorData.detail
    const message =
      typeof detail === 'string'
        ? detail
        : typeof detail?.message === 'string'
          ? detail.message
          : 'An API error occurred.'
    throw new ApiError(response.status, message, detail)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * GET a file rather than JSON.
   *
   * `apiClientFetch` always calls `response.json()`, which is right for every
   * other endpoint and wrong for one that returns a PDF. This shares the parts
   * that matter — the bearer token, the refresh-error path and the 401 re-login —
   * and differs only in what it does with the body.
   *
   * Returns the blob and the filename the server chose, because
   * `Content-Disposition` is the server's answer to "what is this file called"
   * and the client should not invent a second one.
   */
  getFile: async (
    endpoint: string,
    options?: RequestInit
  ): Promise<{ blob: Blob; filename: string | null }> => {
    const session = await getSession()
    const headers = new Headers(options?.headers || {})
    if (session?.accessToken) {
      headers.set('Authorization', `Bearer ${session.accessToken}`)
    }
    if (session?.error === 'RefreshAccessTokenError') {
      await signIn('keycloak')
      throw new Error('Session expired. Please sign in again.')
    }

    const response = await fetch(`${API_URL}${BASE_PATH}${endpoint}`, {
      ...options,
      method: 'GET',
      headers,
    })

    if (response.status === 401) {
      await signIn('keycloak')
      throw new Error('Your session has expired. Please sign in again.')
    }
    if (!response.ok) {
      // The error body is JSON even when the success body is not, so the
      // endpoint's own message still reaches the person who asked for the file.
      const errorData = await response.json().catch(() => ({
        detail: response.statusText,
      }))
      throw new Error(errorData.detail || 'The file could not be downloaded.')
    }

    const disposition = response.headers.get('Content-Disposition') || ''
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
    return {
      blob: await response.blob(),
      filename: match ? decodeURIComponent(match[1]) : null,
    }
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * POST a `multipart/form-data` body.
   *
   * Separate from `post` because that one sets `Content-Type: application/json`
   * and stringifies. A multipart request must let `fetch` set the header
   * itself: the boundary token is generated with the body, so a hand-set
   * `Content-Type` sends a boundary the body does not use and the server parses
   * no fields at all.
   */
  postForm: <T = unknown>(
    endpoint: string,
    form: FormData,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: form,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(
    endpoint: string,
    data?: unknown,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch
